// Admin detail for a tutor — info, courses taught, sessions and students taught. Rendered by UserDetailPage.
import { useFetch } from "../../hooks/useApi.js";
import Badge from "../../components/shared/Badge.js";
import DetailHeader from "../../components/admin/DetailHeader.js";
import DetailSection from "../../components/admin/DetailSection.js";
import InfoField from "../../components/admin/InfoField.js";
import { formatDate, formatDateTime } from "../../lib/formatDateTime.js";
import { specialisationLabel } from "../../lib/profileFormOptions.js";
import type { AdminUserRecord, TutorDetail } from "@dm-lms/shared/types";

const TABLE = "min-w-full text-sm";
const HEAD = "bg-gray-50 text-left text-dm-slate";
const TH = "px-4 py-3 font-medium";
const TD = "px-4 py-3";

export default function TutorDetailPage({ user }: { user: Extract<AdminUserRecord, { role: "tutor" }> }) {
  const { data, isLoading, error } = useFetch<TutorDetail>(`/api/admin/users/${user.id}/tutor-detail`);
  const profile = data?.profile ?? null;
  const courses = data?.courses ?? [];
  const sessions = data?.sessions ?? [];
  const students = data?.students ?? [];
  const now = Date.now();   // upcoming = starts after the browser's current time

  const states = { isLoading, error };

  return (
    <div className="max-w-3xl space-y-6">
      <DetailSection header={<DetailHeader user={user} />} {...states}>
        <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
          <InfoField label="Tutor code">{user.code}</InfoField>
          <InfoField label="Member since">{formatDate(user.createdAt)}</InfoField>
          <InfoField label="Phone">{profile?.phone ?? "—"}</InfoField>
          <InfoField label="Specialisation">{specialisationLabel(profile?.specialisation ?? null)}</InfoField>
          <InfoField label="Qualifications">{profile?.qualifications ?? "—"}</InfoField>
          <InfoField label="Bio">{profile?.bio ?? "—"}</InfoField>
        </div>
      </DetailSection>

      <DetailSection title="Courses taught" isEmpty={courses.length === 0} emptyText="No courses yet." {...states}>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className={TABLE}>
            <thead className={HEAD}>
              <tr>
                <th className={TH}>Code</th><th className={TH}>Title</th><th className={TH}>Mode</th>
                <th className={TH}>Status</th><th className={TH}>Enrolled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {courses.map((course) => (
                <tr key={course.code}>
                  <td className={`${TD} font-medium text-dm-navy`}>{course.code}</td>
                  <td className={TD}>{course.title}</td>
                  <td className={`${TD} capitalize`}>{course.mode}</td>
                  <td className={TD}><Badge tone={course.isPublished ? "green" : "gray"}>{course.isPublished ? "Published" : "Draft"}</Badge></td>
                  <td className={TD}>{course.enrolledCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DetailSection>

      <DetailSection title="Sessions" isEmpty={sessions.length === 0} emptyText="No sessions yet." {...states}>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className={TABLE}>
            <thead className={HEAD}>
              <tr>
                <th className={TH}>Title</th><th className={TH}>Date</th><th className={TH}>Course</th><th className={TH}>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sessions.map((session) => {
                const upcoming = new Date(session.scheduledAt).getTime() > now;
                return (
                  <tr key={`${session.courseCode}-${session.scheduledAt}-${session.title}`}>
                    <td className={`${TD} font-medium text-dm-navy`}>{session.title}</td>
                    <td className={TD}>{formatDateTime(session.scheduledAt)}</td>
                    <td className={TD}>{session.courseCode}</td>
                    <td className={TD}><Badge tone={upcoming ? "blue" : "gray"}>{upcoming ? "Upcoming" : "Past"}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DetailSection>

      <DetailSection title="Students taught" isEmpty={students.length === 0} emptyText="No students yet." {...states}>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className={TABLE}>
            <thead className={HEAD}>
              <tr><th className={TH}>Code</th><th className={TH}>Name</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {students.map((student) => (
                <tr key={student.code}>
                  <td className={`${TD} font-medium text-dm-navy`}>{student.code}</td>
                  <td className={TD}>{student.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DetailSection>
    </div>
  );
}
